import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CheckSquare, Plus, Trash2 } from "lucide-react";
import { useState } from "react";
import { api } from "../lib/api";
import type { ParaType, Project, Task, TaskStatus } from "../lib/types";

const COLUMNS: { key: TaskStatus; label: string; accent: string }[] = [
  { key: "todo", label: "Todo", accent: "border-zinc-700" },
  { key: "doing", label: "Doing", accent: "border-amber-600" },
  { key: "done", label: "Done", accent: "border-emerald-600" },
  { key: "cancelled", label: "Cancelled", accent: "border-zinc-800" },
];

const PARA_TYPES: ParaType[] = ["project", "area", "resource", "archive"];

interface Props {
  onOpenNote: (id: string) => void;
}

export function KanbanView({ onOpenNote }: Props) {
  const qc = useQueryClient();
  const [filterProject, setFilterProject] = useState<string | null>(null);

  const projectsQ = useQuery({
    queryKey: ["projects"],
    queryFn: () => api.listProjects(),
  });
  const tasksQ = useQuery({
    queryKey: ["tasks", filterProject],
    queryFn: () =>
      api.listTasks(filterProject ? { project_id: filterProject } : {}),
  });

  const statusMut = useMutation({
    mutationFn: ({ id, status }: { id: string; status: TaskStatus }) =>
      api.setTaskStatus(id, status),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["tasks"] }),
  });
  const createMut = useMutation({
    mutationFn: (title: string) =>
      api.createTask({ title, project_id: filterProject ?? undefined }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["tasks"] }),
  });
  const deleteMut = useMutation({
    mutationFn: (id: string) => api.deleteTask(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["tasks"] }),
  });
  const createProjectMut = useMutation({
    mutationFn: (body: { name: string; para_type: ParaType }) => api.createProject(body),
    onSuccess: (p) => {
      qc.invalidateQueries({ queryKey: ["projects"] });
      setFilterProject(p.id);
    },
  });

  function addTask() {
    const title = window.prompt("Titel der Task?");
    if (!title) return;
    createMut.mutate(title);
  }
  function addProject() {
    const name = window.prompt("Name des Projekts?");
    if (!name) return;
    const type = window.prompt("Typ (project/area/resource/archive)?", "project") as ParaType | null;
    if (!type || !PARA_TYPES.includes(type)) return;
    createProjectMut.mutate({ name, para_type: type });
  }

  const tasks = tasksQ.data ?? [];
  const byCol: Record<TaskStatus, Task[]> = { todo: [], doing: [], done: [], cancelled: [] };
  for (const t of tasks) byCol[t.status].push(t);

  return (
    <div className="flex h-full flex-col">
      <header className="flex items-center gap-3 border-b border-zinc-800 bg-zinc-950 px-4 py-2 text-xs">
        <CheckSquare className="h-4 w-4 text-emerald-400" />
        <span className="text-zinc-400">Projekt:</span>
        <select
          value={filterProject ?? ""}
          onChange={(e) => setFilterProject(e.target.value || null)}
          className="rounded border border-zinc-800 bg-zinc-900 px-2 py-1"
        >
          <option value="">— Alle —</option>
          {(["project", "area", "resource", "archive"] as ParaType[]).map((pt) => (
            <ProjectOptGroup key={pt} type={pt} projects={projectsQ.data ?? []} />
          ))}
        </select>
        <button
          onClick={addProject}
          className="flex items-center gap-1 rounded border border-zinc-800 px-2 py-1 hover:border-emerald-500 hover:text-emerald-400"
        >
          <Plus className="h-3 w-3" />
          Projekt
        </button>
        <button
          onClick={addTask}
          className="flex items-center gap-1 rounded border border-zinc-800 px-2 py-1 hover:border-emerald-500 hover:text-emerald-400"
        >
          <Plus className="h-3 w-3" />
          Task
        </button>
        <span className="ml-auto text-zinc-500">{tasks.length} Tasks</span>
      </header>

      <div className="flex flex-1 gap-2 overflow-x-auto p-3">
        {COLUMNS.map((col) => (
          <div
            key={col.key}
            className={`flex w-72 shrink-0 flex-col rounded-lg border bg-zinc-950/50 ${col.accent}`}
          >
            <div className="flex items-center justify-between border-b border-zinc-800 px-3 py-2 text-[10px] font-medium uppercase tracking-wider text-zinc-400">
              <span>{col.label}</span>
              <span className="text-zinc-600">{byCol[col.key].length}</span>
            </div>
            <ul className="flex-1 space-y-2 overflow-y-auto p-2">
              {byCol[col.key].map((t) => (
                <TaskCard
                  key={t.id}
                  task={t}
                  projects={projectsQ.data ?? []}
                  onMove={(next) => statusMut.mutate({ id: t.id, status: next })}
                  onDelete={() => {
                    if (window.confirm(`"${t.title}" löschen?`)) deleteMut.mutate(t.id);
                  }}
                  onOpenNote={onOpenNote}
                />
              ))}
              {byCol[col.key].length === 0 && (
                <li className="rounded border border-dashed border-zinc-800 p-3 text-center text-[10px] italic text-zinc-700">
                  leer
                </li>
              )}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
}

function ProjectOptGroup({ type, projects }: { type: ParaType; projects: Project[] }) {
  const filtered = projects.filter((p) => p.para_type === type);
  if (filtered.length === 0) return null;
  return (
    <optgroup label={type.toUpperCase()}>
      {filtered.map((p) => (
        <option key={p.id} value={p.id}>
          {p.name}
        </option>
      ))}
    </optgroup>
  );
}

function TaskCard({
  task,
  projects,
  onMove,
  onDelete,
  onOpenNote,
}: {
  task: Task;
  projects: Project[];
  onMove: (next: TaskStatus) => void;
  onDelete: () => void;
  onOpenNote: (id: string) => void;
}) {
  const project = projects.find((p) => p.id === task.project_id);
  const next: TaskStatus | null =
    task.status === "todo"
      ? "doing"
      : task.status === "doing"
      ? "done"
      : task.status === "done"
      ? null
      : null;

  return (
    <li className="group rounded border border-zinc-800 bg-zinc-900/60 p-2 text-xs">
      <div className="flex items-start justify-between gap-1">
        <div className="flex-1 text-zinc-100">{task.title}</div>
        <button
          onClick={onDelete}
          className="text-zinc-600 opacity-0 group-hover:opacity-100 hover:text-red-400"
        >
          <Trash2 className="h-3 w-3" />
        </button>
      </div>
      <div className="mt-1 flex flex-wrap items-center gap-1 text-[10px] text-zinc-500">
        {project && (
          <span
            className="rounded bg-zinc-800 px-1 py-0.5"
            style={project.color ? { color: project.color } : undefined}
          >
            {project.name}
          </span>
        )}
        {task.note_id && (
          <button
            onClick={() => onOpenNote(task.note_id!)}
            className="rounded border border-zinc-700 px-1 py-0.5 hover:text-emerald-400"
          >
            note
          </button>
        )}
        {task.priority > 0 && <span>P{task.priority}</span>}
        {next && (
          <button
            onClick={() => onMove(next)}
            className="ml-auto rounded border border-zinc-700 px-1 py-0.5 hover:border-emerald-500 hover:text-emerald-400"
          >
            → {next}
          </button>
        )}
        {task.status === "doing" && (
          <button
            onClick={() => onMove("todo")}
            className="rounded border border-zinc-700 px-1 py-0.5 hover:border-zinc-500"
          >
            ← todo
          </button>
        )}
      </div>
    </li>
  );
}
